import { Alert, Button, Card, Descriptions, Skeleton, Space, Tag, Timeline, Typography } from "antd";
import { StopOutlined, SwapOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { formatDate } from "../app/classes.ts";
import type { StudentProfile } from "../app/students.ts";
import { PlacementModal, placementKeys, type Placement } from "./PlacementModal.tsx";

/** The student's class today, the dated history of classes, and the place/move and end actions. */
export function StudentClassTab({ student, canManage }: { student: StudentProfile; canManage: boolean }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"place" | "end">();

  const history = useQuery({
    queryKey: placementKeys.history(student.id),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/students/{id}/placements", { params: { path: { id: student.id } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });

  const current = student.currentPlacement;
  const open = history.data?.find((p) => p.to == null);
  const isActive = student.status === "Active";
  const timelineItem = (p: Placement) => {
    const ongoing = p.to == null;
    return {
      key: p.id,
      color: ongoing ? "green" : "gray",
      title: t("placement.period", { from: formatDate(p.from), to: ongoing ? t("placement.ongoing") : formatDate(p.to) }),
      content: (
        <Space orientation="vertical" size={0}>
          <Space>
            <Link to={`/academic/classes/${p.classId}`}>{p.className}</Link>
            {ongoing && <Tag color="green">{t("classes.current")}</Tag>}
          </Space>
          <Typography.Text type="secondary">{t("placement.details", { grade: p.grade, course: p.courseName, year: p.academicYearLabel })}</Typography.Text>
        </Space>
      ),
    };
  };

  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Card size="small" title={t("placement.current")}>
        {current ? (
          <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }}>
            <Descriptions.Item label={t("placement.class")}>
              <Link to={`/academic/classes/${current.classId}`}>{current.className}</Link>
            </Descriptions.Item>
            <Descriptions.Item label={t("placement.grade")}>{current.grade}</Descriptions.Item>
            <Descriptions.Item label={t("placement.course")}>{current.courseName}</Descriptions.Item>
            <Descriptions.Item label={t("placement.since")}>{formatDate(current.from)}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Typography.Text type="secondary">{t("placement.none")}</Typography.Text>
        )}
        {canManage && (
          <Space wrap style={{ marginTop: 16 }}>
            <Button type="primary" icon={<SwapOutlined />} disabled={!isActive} onClick={() => setMode("place")}>
              {t("placement.place")}
            </Button>
            {open && (
              <Button danger icon={<StopOutlined />} onClick={() => setMode("end")}>
                {t("placement.end")}
              </Button>
            )}
          </Space>
        )}
        {canManage && !isActive && <Alert type="info" showIcon style={{ marginTop: 16 }} title={t("placement.notActive")} />}
      </Card>

      <Card size="small" title={t("placement.history")}>
        {history.isPending && <Skeleton active paragraph={{ rows: 3 }} />}
        {history.isError && (
          <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void history.refetch()}>{t("app.retry")}</Button>} />
        )}
        {history.data && history.data.length === 0 && <Typography.Text type="secondary">{t("placement.historyEmpty")}</Typography.Text>}
        {history.data && history.data.length > 0 && <Timeline items={history.data.map(timelineItem)} />}
      </Card>

      <PlacementModal studentId={student.id} open={open} mode={mode} onClose={() => setMode(undefined)} />
    </Space>
  );
}
